/**
 * sqlite3mt bootstrap/runtime generation helpers
 *
 * Narrow mechanical extraction from §96 §5, §6.4, §7.0, §7.1, §7.2, §7.3,
 * §7.4–§7.6, §7.8–§7.9 and §19.2.
 *
 * This module codifies the bootstrap-specific control-plane contract:
 * - fresh generations start from a known volatile/SAB baseline
 * - stale SAB control/ack/lock state is cleared before CONTROL_READY = 1
 * - runtime epoch publication is explicit
 * - durable reconcile intents are classified, verified, and replayed
 * - snapshot restore and journal-first/intent-first synthesis (§7.2)
 * - durable local-commit journal replay with WAL-cycle fold-before-insert (§7.3)
 *
 * §7.1 orchestrator:
 * - runBootstrap() composes all 9 normative steps in the prescribed order
 * - individual helpers exist for each step; the orchestrator is the composition
 */

import {
    DEFAULT_PAGE_SIZE,
    NUM_SLOTS,
    SAB2_DATA_OFF,
    SAB1_DB_PAGE_COUNT_I32,
    SAB1_PAGE_TABLE_LOCK_I32,
    Sab4Scalar,
    SlotMetaI32,
    sab1CtrlView,
    sab1LoadSlotGen,
    sab1SlotMetaView,
    sab1StoreSlotGen,
    sab3ResetLocks,
    sab2SizeView,
    sab4ResetConnSlots,
    sab4RuntimeEpochView,
    sab4ScalarView,
} from './sab-layout'
import {
    applyDeltaIntoWorkspaceOverlay,
    applyBackfilledPending,
    equalDelta,
    foldAllPendingIntoDbOverlay,
    insertPendingDeltaOrdered,
    noteObservedWalTipFromAppend,
    setDbPageCountAndInvalidateEofIfNeeded,
} from './dbview'
import { publishFrontierStateToSabIfChanged } from './frontier'
import type {
    DurableLocalCommitJournalEntry,
    DurableLocalSnapshot,
    DurableLocalStore,
} from './durable'
import {
    SlotState,
    type CommittedTx,
    type ControlWorkerState,
    type CheckpointMode,
    type Delta,
    type FrameMax,
    type PageHash,
    type Pgno,
    type PublicHead,
    type ReconcileIntentPhase,
    type Root,
    type WalSnapshot,
    sameRoot,
    sameWalSalt,
} from './types'
import {
    scanWalCommittedTransactions,
    scanExactCommittedTransaction,
    type HashPageFn,
    txMatchesIgnoredIntent,
} from './wal-scan'

/**
 * Informative write-gate reason values stored in SAB4.
 * The bootstrap reason is the only normative one needed for this slice.
 */
export const WRITE_GATE_REASON_NONE = 0
export const WRITE_GATE_REASON_BOOTSTRAP_IN_PROGRESS = 1

export interface Deferred<T> {
    promise: Promise<T>
    resolve(value?: T | PromiseLike<T>): void
    reject(reason?: unknown): void
}

export interface PendingBaseAdvance {
    kind: 'PUSH' | 'RECONCILE'
    intentId: bigint | null
    promise: Promise<void>
}

/**
 * Durable reconcile intent contract from §6.4.
 * This is the durable record bootstrap uses to resolve unfinished imports.
 */
export interface DurableReconcileIntent {
    intentId: bigint
    phase: ReconcileIntentPhase
    baseRoot: Root
    baseVersion: bigint
    basePageCount: number
    remoteRoot: Root
    remoteVersion: bigint
    walSalt0: number
    walSalt1: number
    caughtUpFrameMax: number
    importDelta: Delta
    residualOverlay: Map<Pgno, PageHash>
    residualPageCount: number
    attemptStartFrame: number | null
    attemptEndFrame: number | null
    attemptWalSalt0: number | null
    attemptWalSalt1: number | null
    commitFrameMax: number | null
    commitPostWalSalt0: number | null
    commitPostWalSalt1: number | null
}

/**
 * Fresh-generation volatile state that must be reinitialized before normal
 * runtime activity starts in a new Control Worker generation.
 */
export interface FreshGenerationVolatileState {
    pushBusy: boolean
    mergeBusy: boolean
    checkpointBusy: boolean
    compactBusy: boolean
    compactWanted: boolean
    checkpointWanted: boolean
    checkpointWantedMode: CheckpointMode | null
    checkpointInflightSeq: number
    checkpointNoProgressCount: number
    checkpointQuiescentBusy: boolean
    pendingBaseAdvance: PendingBaseAdvance | null
    reconcileIntent: DurableReconcileIntent | null
    inflightCommitRegistrations: number
    headAdvancePreparing: boolean
    headAdvancePrepareWaiter: Deferred<void> | null
    commitRegistrationDrainWaiter: Deferred<void> | null
    snapshotPersistTail: Promise<void>
    snapshotPersistBusy: boolean
    snapshotPersistWanted: boolean
    remoteRefreshReqSeq: bigint
    remoteRefreshAppliedReqSeq: bigint
    syncRetryScheduled: boolean
}

export interface BootstrapRuntimeControl {
    syncFatalError: unknown | null
}

export interface FreshBootstrapPrimerArgs {
    sab2: SharedArrayBuffer
    sab3: SharedArrayBuffer
    sab4: SharedArrayBuffer
    runtime: BootstrapRuntimeControl
    walBytes: Uint8Array
    previousRuntimeEpoch?: bigint | null
    nowMs?: number
    writeGateReason?: number
}

export interface FreshBootstrapPrimerResult {
    runtimeEpoch: bigint
    volatileState: FreshGenerationVolatileState
}

export interface BootstrapIntentDisposition {
    activeIntentOrNull: DurableReconcileIntent | null
    ignoredIntentOrNull: DurableReconcileIntent | null
}

export interface ReconcileIntentCommitAttempt {
    commitFrameMax: number
    commitPostWalSalt0: number
    commitPostWalSalt1: number
}

export interface BootstrapRecoveryDurableStore {
    markReconcileIntentCommitted(
        intentId: bigint,
        committed: ReconcileIntentCommitAttempt
    ): Promise<void> | void
}

export interface VerifyAppendingIntentAndMaybePromoteArgs {
    intent: DurableReconcileIntent
    live: WalSnapshot | null
    walBytes: Uint8Array
    durable: BootstrapRecoveryDurableStore
    hashPage: HashPageFn
    enterFatalRuntime(error: unknown, reason: string): void
    pageSize?: number
}

export interface BootstrapRecoveredState extends Pick<
    ControlWorkerState,
    'dbOverlay'
    | 'dbPageCount'
    | 'workspaceBaseHead'
    | 'workspaceOverlay'
    | 'workspacePageCount'
    | 'pendingDbDeltas'
    | 'currentWalSalt0'
    | 'currentWalSalt1'
    | 'integratedFrameMax'
    | 'lastWalSnapshot'
> {}

export interface ApplyCommittedIntentToLiveStateArgs {
    intent: DurableReconcileIntent
    state: BootstrapRecoveredState
    sab1: SharedArrayBuffer
    pageSize?: number
}

export const BOOTSTRAP_PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT =
    'BOOTSTRAP_PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT'
export const BOOTSTRAP_APPENDING_INTENT_WAL_CORRUPTION =
    'BOOTSTRAP_APPENDING_INTENT_WAL_CORRUPTION'
export const BOOTSTRAP_NO_RECOVERABLE_LOCAL_STATE =
    'BOOTSTRAP_NO_RECOVERABLE_LOCAL_STATE'
export const BOOTSTRAP_JOURNAL_BASE_MISMATCH =
    'BOOTSTRAP_JOURNAL_BASE_MISMATCH'
export const BOOTSTRAP_WAL_DELTA_MISMATCH =
    'BOOTSTRAP_WAL_DELTA_MISMATCH'

/**
 * Full mutable state target for §7.2 snapshot restore / synthesis
 * and §7.3 journal replay.
 *
 * This extends BootstrapRecoveredState with dbBaseRoot and localCommitSeq
 * which are needed by the restore and replay paths. The helper also clears
 * stale bootstrap-only base-advance/intent pointers on successful restore.
 */
export interface RestoreTargetState {
    dbBaseRoot: Root
    dbOverlay: Map<Pgno, PageHash>
    dbPageCount: number
    workspaceBaseHead: PublicHead
    workspaceOverlay: Map<Pgno, PageHash>
    workspacePageCount: number
    pendingDbDeltas: Map<FrameMax, Delta>
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
    lastWalSnapshot: WalSnapshot | null
    localCommitSeq: bigint
    pendingBaseAdvance?: PendingBaseAdvance | null
    reconcileIntent?: DurableReconcileIntent | null
}

export interface RestoreSnapshotOrSynthesizeBaseArgs {
    state: RestoreTargetState
    snap: DurableLocalSnapshot | null
    journalTailIfNoSnap: readonly DurableLocalCommitJournalEntry[]
    intent: DurableReconcileIntent | null
    enterFatalRuntime: (error: unknown, reason: string) => void
}

export function remoteHeadRewindsRestoredLocalState(
    restoredLocalHead: PublicHead,
    remoteHead: PublicHead
): boolean {
    return remoteHead.version < restoredLocalHead.version
}

export function resetBootstrapStateToRemoteHead(
    state: Pick<
        BootstrapMutableState,
        'dbBaseRoot'
        | 'dbOverlay'
        | 'dbPageCount'
        | 'workspaceBaseHead'
        | 'workspaceOverlay'
        | 'workspacePageCount'
        | 'pendingDbDeltas'
        | 'currentWalSalt0'
        | 'currentWalSalt1'
        | 'integratedFrameMax'
        | 'lastWalSnapshot'
        | 'localCommitSeq'
        | 'remoteHead'
        | 'pendingBaseAdvance'
        | 'reconcileIntent'
        | 'stagedCas'
        | 'recoveryChangedState'
    >,
    remoteHead: PublicHead
): void {
    state.dbBaseRoot = remoteHead.root
    state.dbOverlay.clear()
    state.dbPageCount = remoteHead.root.meta.pageCount

    state.workspaceBaseHead = {
        root: remoteHead.root,
        version: remoteHead.version,
    }
    state.workspaceOverlay.clear()
    state.workspacePageCount = remoteHead.root.meta.pageCount

    state.pendingDbDeltas.clear()
    state.currentWalSalt0 = null
    state.currentWalSalt1 = null
    state.integratedFrameMax = 0
    state.lastWalSnapshot = null
    state.localCommitSeq = 0n
    state.remoteHead = {
        root: remoteHead.root,
        version: remoteHead.version,
    }
    state.pendingBaseAdvance = null
    state.reconcileIntent = null
    state.stagedCas.clear()
    state.recoveryChangedState = true
}

export interface ReplayDurableLocalCommitJournalArgs {
    state: RestoreTargetState
    tail: readonly DurableLocalCommitJournalEntry[]
    sab1: SharedArrayBuffer
    pageSize?: number
    enterFatalRuntime: (error: unknown, reason: string) => void
    hydrateBlobs?: (hashes: Iterable<PageHash>) => void
}

export interface BootstrapLiveWalRecoveryState extends Pick<
    RestoreTargetState,
    | 'dbOverlay'
    | 'dbPageCount'
    | 'pendingDbDeltas'
    | 'currentWalSalt0'
    | 'currentWalSalt1'
    | 'integratedFrameMax'
    | 'lastWalSnapshot'
> {}

export interface PrepareRecoveredStateForLiveWalArgs {
    state: BootstrapLiveWalRecoveryState
    live: WalSnapshot
    sab1: SharedArrayBuffer
    pageSize?: number
}

export interface ResetRecoveredStateWhenLiveWalMissingArgs {
    state: BootstrapLiveWalRecoveryState
    sab1: SharedArrayBuffer
    pageSize?: number
}

export interface BootstrapGhostRecoveryTransaction {
    putBlob(hash: PageHash, bytes: Uint8Array): void | Promise<void>
    appendLocalCommit(entry: DurableLocalCommitJournalEntry): void | Promise<void>
}

export interface BootstrapGhostRecoveryDurableStore {
    transaction<T>(
        callback: (tx: BootstrapGhostRecoveryTransaction) => Promise<T> | T
    ): Promise<T>
}

export interface RecoverGhostLocalCommitsFromWalArgs {
    state: RestoreTargetState
    live: WalSnapshot | null
    ignoredIntent: DurableReconcileIntent | null
    walBytes: Uint8Array
    durable: BootstrapGhostRecoveryDurableStore
    stagedCas: Map<PageHash, Uint8Array>
    hashPage: HashPageFn
    sab1: SharedArrayBuffer
    sab4: SharedArrayBuffer
    pageSize?: number
    hydrateRecoveredWalBlobs?: (rec: CommittedTx) => Promise<void> | void
    enterFatalRuntime(error: unknown, reason: string): void
}

export interface ReconcileRestoredStateAgainstLiveWalArgs {
    state: RestoreTargetState
    live: WalSnapshot | null
    sab1: SharedArrayBuffer
    sab4: SharedArrayBuffer
    pageSize?: number
}

/**
 * Fresh runtime epochs must never go backwards, even if the clock stalls or a
 * caller provides a previous epoch that is ahead of the current timestamp.
 */
export function chooseFreshRuntimeEpoch(
    previousEpoch: bigint | null = null,
    nowMs: number = Date.now()
): bigint {
    const candidate = BigInt(nowMs) << 20n
    if (previousEpoch != null && candidate <= previousEpoch) {
        return previousEpoch + 1n
    }
    return candidate
}

/**
 * §7.2: Restore snapshot or synthesize base state from journal/intent.
 *
 * Three fallback paths in normative priority order:
 *  1. snapshot → restore all persisted fields directly
 *  2. first journal entry → synthesize base from workspace base fields
 *  3. reconcile intent → synthesize base from intent base fields
 *  4. none of the above → fatal: no recoverable local state
 */
export function restoreSnapshotOrSynthesizeBase({
    state,
    snap,
    journalTailIfNoSnap,
    intent,
    enterFatalRuntime,
}: RestoreSnapshotOrSynthesizeBaseArgs): void {
    if (snap != null) {
        state.dbBaseRoot = snap.dbBaseRoot
        state.dbOverlay = new Map(snap.dbOverlay)
        state.dbPageCount = snap.dbPageCount
        state.workspaceBaseHead = snap.workspaceBaseHead
        state.workspaceOverlay = new Map(snap.workspaceOverlay)
        state.workspacePageCount = snap.workspacePageCount
        state.pendingDbDeltas = new Map(snap.pendingDbDeltas)
        state.currentWalSalt0 = snap.currentWalSalt0
        state.currentWalSalt1 = snap.currentWalSalt1
        state.integratedFrameMax = snap.integratedFrameMax
        state.lastWalSnapshot = snap.lastWalSnapshot == null
            ? null
            : { ...snap.lastWalSnapshot }
        state.localCommitSeq = snap.localCommitSeq
        state.pendingBaseAdvance = null
        state.reconcileIntent = null
        return
    }

    if (journalTailIfNoSnap.length > 0) {
        const first = journalTailIfNoSnap[0]
        state.dbBaseRoot = first.workspaceBaseRoot
        state.dbOverlay.clear()
        state.dbPageCount = first.workspaceBasePageCount
        state.workspaceBaseHead = {
            root: first.workspaceBaseRoot,
            version: first.workspaceBaseVersion,
        }
        state.workspaceOverlay.clear()
        state.workspacePageCount = first.workspaceBasePageCount
        state.pendingDbDeltas.clear()
        state.currentWalSalt0 = null
        state.currentWalSalt1 = null
        state.integratedFrameMax = 0
        state.lastWalSnapshot = null
        state.localCommitSeq = 0n
        state.pendingBaseAdvance = null
        state.reconcileIntent = null
        return
    }

    if (intent != null) {
        state.dbBaseRoot = intent.baseRoot
        state.dbOverlay.clear()
        state.dbPageCount = intent.basePageCount
        state.workspaceBaseHead = {
            root: intent.baseRoot,
            version: intent.baseVersion,
        }
        state.workspaceOverlay.clear()
        state.workspacePageCount = intent.basePageCount
        state.pendingDbDeltas.clear()
        state.currentWalSalt0 = null
        state.currentWalSalt1 = null
        state.integratedFrameMax = 0
        state.lastWalSnapshot = null
        state.localCommitSeq = 0n
        state.pendingBaseAdvance = null
        state.reconcileIntent = null
        return
    }

    enterFatalRuntime(BOOTSTRAP_NO_RECOVERABLE_LOCAL_STATE, 'BOOT_FATAL')
}

/**
 * §7.3: Replay the durable local-commit journal into restored state.
 *
 * For each journal entry (in commitSeq order):
 *  1. Verify base match (root, version, page count) — fatal if mismatched
 *  2. Hydrate page blobs into staged CAS
 *  3. Fold prior WAL cycle if salt changed (R44: fold-before-insert)
 *  4. Insert pending delta and apply to workspace overlay
 *  5. Track tip, integratedFrameMax, and localCommitSeq
 */
export function replayDurableLocalCommitJournal({
    state,
    tail,
    sab1,
    pageSize = DEFAULT_PAGE_SIZE,
    enterFatalRuntime,
    hydrateBlobs,
}: ReplayDurableLocalCommitJournalArgs): void {
    for (const entry of tail) {
        if (
            !sameRoot(entry.workspaceBaseRoot, state.workspaceBaseHead.root)
            || entry.workspaceBaseVersion !== state.workspaceBaseHead.version
            || entry.workspaceBasePageCount !== state.workspacePageCount
        ) {
            enterFatalRuntime(BOOTSTRAP_JOURNAL_BASE_MISMATCH, 'BOOT_FATAL')
            return
        }

        // Hydrate page blobs for this entry's delta
        if (hydrateBlobs != null && entry.delta.pageHashes.size > 0) {
            hydrateBlobs(entry.delta.pageHashes.values())
        }

        // §7.3 / R44: WAL-cycle fold-before-insert
        if (state.currentWalSalt0 == null) {
            state.currentWalSalt0 = entry.walSalt0
            state.currentWalSalt1 = entry.walSalt1
            state.integratedFrameMax = 0
        } else if (!sameWalSalt(
            state.currentWalSalt0,
            state.currentWalSalt1,
            entry.walSalt0,
            entry.walSalt1
        )) {
            const dbPageCountRef = { value: state.dbPageCount }
            foldAllPendingIntoDbOverlay(
                sab1,
                state.pendingDbDeltas,
                state.dbOverlay,
                dbPageCountRef,
                pageSize
            )
            state.dbPageCount = dbPageCountRef.value
            state.currentWalSalt0 = entry.walSalt0
            state.currentWalSalt1 = entry.walSalt1
            state.integratedFrameMax = 0
        }

        insertPendingDeltaOrdered(
            state.pendingDbDeltas,
            entry.frameMax,
            entry.delta
        )

        const workspacePageCountRef = { value: state.workspacePageCount }
        applyDeltaIntoWorkspaceOverlay(
            state.workspaceOverlay,
            workspacePageCountRef,
            entry.delta
        )
        state.workspacePageCount = workspacePageCountRef.value

        noteObservedWalTipFromAppend(
            state,
            entry.walSalt0,
            entry.walSalt1,
            entry.frameMax
        )
        state.integratedFrameMax = Math.max(state.integratedFrameMax, entry.frameMax)
        state.localCommitSeq = entry.commitSeq > state.localCommitSeq
            ? entry.commitSeq
            : state.localCommitSeq
    }
}

/**
 * §7.4: Decide whether the restored workspace base already dominates or
 * reflects a durable reconcile intent's remote target.
 *
 * Same-version, different-root is fatal protocol corruption.
 */
export function snapshotDominatesOrReflectsIntent(
    workspaceBaseHead: PublicHead,
    intent: Pick<DurableReconcileIntent, 'remoteRoot' | 'remoteVersion'>,
    enterFatalRuntime: (error: unknown, reason: string) => void
): boolean {
    if (workspaceBaseHead.version > intent.remoteVersion) {
        return true
    }

    if (workspaceBaseHead.version < intent.remoteVersion) {
        return false
    }

    if (!sameRoot(workspaceBaseHead.root, intent.remoteRoot)) {
        enterFatalRuntime(
            BOOTSTRAP_PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT,
            'BOOT_FATAL'
        )
        return false
    }

    return true
}

/**
 * §7.4: Resolve whether a durable reconcile intent is active during bootstrap
 * recovery or can be ignored as already reflected by restored local state.
 */
export function resolveIntentDisposition(
    workspaceBaseHead: PublicHead,
    loadedIntent: DurableReconcileIntent | null,
    enterFatalRuntime: (error: unknown, reason: string) => void
): BootstrapIntentDisposition {
    if (loadedIntent == null) {
        return { activeIntentOrNull: null, ignoredIntentOrNull: null }
    }

    if (snapshotDominatesOrReflectsIntent(workspaceBaseHead, loadedIntent, enterFatalRuntime)) {
        return {
            activeIntentOrNull: null,
            ignoredIntentOrNull: loadedIntent,
        }
    }

    if (loadedIntent.phase === 'COMMITTED' || loadedIntent.phase === 'APPENDING') {
        return {
            activeIntentOrNull: loadedIntent,
            ignoredIntentOrNull: null,
        }
    }

    return {
        activeIntentOrNull: null,
        ignoredIntentOrNull: loadedIntent,
    }
}

/**
 * §7.5: Verify a durable APPENDING intent against the visible valid WAL
 * prefix and durably promote it to COMMITTED when an exact match is found.
 *
 * Returns true only when bootstrap changed durable/runtime state by
 * promoting the intent to COMMITTED.
 */
export async function verifyAppendingIntentAndMaybePromote({
    intent,
    live,
    walBytes,
    durable,
    hashPage,
    enterFatalRuntime,
    pageSize = DEFAULT_PAGE_SIZE,
}: VerifyAppendingIntentAndMaybePromoteArgs): Promise<boolean> {
    if (live == null) {
        return false
    }

    const attemptStartFrame = intent.attemptStartFrame
    const attemptEndFrame = intent.attemptEndFrame
    const attemptWalSalt0 = intent.attemptWalSalt0
    const attemptWalSalt1 = intent.attemptWalSalt1

    if (
        attemptStartFrame == null
        || attemptEndFrame == null
        || attemptWalSalt0 == null
        || attemptWalSalt1 == null
    ) {
        throw new Error('APPENDING intent missing attempt metadata')
    }

    if (!sameWalSalt(attemptWalSalt0, attemptWalSalt1, live.salt0, live.salt1)) {
        return false
    }

    if (attemptEndFrame > live.mxFrame) {
        return false
    }

    const exact = scanExactCommittedTransaction(
        walBytes,
        pageSize,
        attemptStartFrame,
        attemptEndFrame,
        attemptWalSalt0,
        attemptWalSalt1,
        intent.importDelta,
        hashPage
    )

    if (exact.kind === 'FOUND_EXACT_MATCH') {
        await durable.markReconcileIntentCommitted(intent.intentId, {
            commitFrameMax: exact.frameMax,
            commitPostWalSalt0: live.salt0,
            commitPostWalSalt1: live.salt1,
        })

        intent.phase = 'COMMITTED'
        intent.commitFrameMax = exact.frameMax
        intent.commitPostWalSalt0 = live.salt0
        intent.commitPostWalSalt1 = live.salt1
        return true
    }

    if (exact.kind === 'CORRUPT') {
        enterFatalRuntime(BOOTSTRAP_APPENDING_INTENT_WAL_CORRUPTION, 'BOOT_FATAL')
    }

    return false
}

/**
 * §7.6: Re-apply a durable COMMITTED reconcile intent into restored bootstrap
 * state, including any imported WAL delta.
 */
export function applyCommittedIntentToLiveState({
    intent,
    state,
    sab1,
    pageSize = DEFAULT_PAGE_SIZE,
}: ApplyCommittedIntentToLiveStateArgs): void {
    state.workspaceBaseHead = {
        root: intent.remoteRoot,
        version: intent.remoteVersion,
    }
    state.workspaceOverlay = new Map(intent.residualOverlay)
    state.workspacePageCount = intent.residualPageCount

    if (intent.commitFrameMax == null) {
        return
    }

    const commitPostWalSalt0 = intent.commitPostWalSalt0
    const commitPostWalSalt1 = intent.commitPostWalSalt1
    if (commitPostWalSalt0 == null || commitPostWalSalt1 == null) {
        throw new Error('COMMITTED intent missing commit WAL metadata')
    }

    if (state.currentWalSalt0 == null) {
        state.currentWalSalt0 = commitPostWalSalt0
        state.currentWalSalt1 = commitPostWalSalt1
        state.integratedFrameMax = 0
    } else if (!sameWalSalt(
        state.currentWalSalt0,
        state.currentWalSalt1,
        commitPostWalSalt0,
        commitPostWalSalt1
    )) {
        const dbPageCountRef = { value: state.dbPageCount }
        foldAllPendingIntoDbOverlay(
            sab1,
            state.pendingDbDeltas,
            state.dbOverlay,
            dbPageCountRef,
            pageSize
        )
        state.dbPageCount = dbPageCountRef.value
        state.currentWalSalt0 = commitPostWalSalt0
        state.currentWalSalt1 = commitPostWalSalt1
        state.integratedFrameMax = 0
    }

    insertPendingDeltaOrdered(
        state.pendingDbDeltas,
        intent.commitFrameMax as FrameMax,
        intent.importDelta
    )
    state.integratedFrameMax = Math.max(state.integratedFrameMax, intent.commitFrameMax)
    noteObservedWalTipFromAppend(
        state,
        commitPostWalSalt0,
        commitPostWalSalt1,
        intent.commitFrameMax
    )
}

/**
 * §7.8/§7.9: Prepare restored bootstrap state for the currently visible live
 * WAL cycle before ghost recovery or backfill reconciliation runs.
 *
 * This helper owns the risky cycle-transition cases:
 * - first visible live cycle after restore
 * - salt change to a different WAL cycle
 * - same-salt shrink/reset of the visible WAL tail
 *
 * @returns true when live.mxFrame still lies ahead of integratedFrameMax and
 * the caller should continue with WAL scanning/reconciliation in this cycle.
 */
export function prepareRecoveredStateForLiveWal({
    state,
    live,
    sab1,
    pageSize = DEFAULT_PAGE_SIZE,
}: PrepareRecoveredStateForLiveWalArgs): boolean {
    const shrinkSameSalt =
        state.lastWalSnapshot != null
        && sameWalSalt(
            state.lastWalSnapshot.salt0,
            state.lastWalSnapshot.salt1,
            live.salt0,
            live.salt1
        )
        && (live.mxFrame < state.lastWalSnapshot.mxFrame
            || live.nBackfill < state.lastWalSnapshot.nBackfill)

    if (state.currentWalSalt0 == null) {
        state.currentWalSalt0 = live.salt0
        state.currentWalSalt1 = live.salt1
        state.integratedFrameMax = 0
    } else if (!sameWalSalt(
        state.currentWalSalt0,
        state.currentWalSalt1,
        live.salt0,
        live.salt1
    ) || shrinkSameSalt) {
        const dbPageCountRef = { value: state.dbPageCount }
        foldAllPendingIntoDbOverlay(
            sab1,
            state.pendingDbDeltas,
            state.dbOverlay,
            dbPageCountRef,
            pageSize
        )
        state.dbPageCount = dbPageCountRef.value
        state.currentWalSalt0 = live.salt0
        state.currentWalSalt1 = live.salt1
        state.integratedFrameMax = 0
    }

    state.lastWalSnapshot = live
    return live.mxFrame > state.integratedFrameMax
}

/**
 * §7.9 (live == null): Collapse any restored WAL-tail view into DB-view and
 * clear WAL-cycle tracking before normal runtime opens.
 *
 * @returns true when the reset changed recovered state or SAB1 EOF
 * publication, allowing the caller to bump recovery-changed bookkeeping.
 */
export function resetRecoveredStateWhenLiveWalMissing({
    state,
    sab1,
    pageSize = DEFAULT_PAGE_SIZE,
}: ResetRecoveredStateWhenLiveWalMissingArgs): boolean {
    const changed = state.pendingDbDeltas.size > 0
        || state.currentWalSalt0 != null
        || state.currentWalSalt1 != null
        || state.integratedFrameMax !== 0
        || state.lastWalSnapshot != null
        || Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32) !== state.dbPageCount

    const dbPageCountRef = { value: state.dbPageCount }
    foldAllPendingIntoDbOverlay(
        sab1,
        state.pendingDbDeltas,
        state.dbOverlay,
        dbPageCountRef,
        pageSize
    )
    setDbPageCountAndInvalidateEofIfNeeded(
        sab1,
        state.dbOverlay,
        dbPageCountRef,
        dbPageCountRef.value,
        pageSize
    )
    state.dbPageCount = dbPageCountRef.value
    state.currentWalSalt0 = null
    state.currentWalSalt1 = null
    state.integratedFrameMax = 0
    state.lastWalSnapshot = null
    return changed
}

/**
 * §7.8: Recover committed WAL transactions that are visible in the live valid
 * prefix but missing from the durable local-commit journal.
 *
 * Returns true only when bootstrap had to synthesize at least one durable
 * ghost local commit.
 */
export async function recoverGhostLocalCommitsFromWal({
    state,
    live,
    ignoredIntent,
    walBytes,
    durable,
    stagedCas,
    hashPage,
    sab1,
    sab4,
    pageSize = DEFAULT_PAGE_SIZE,
    hydrateRecoveredWalBlobs,
    enterFatalRuntime,
}: RecoverGhostLocalCommitsFromWalArgs): Promise<boolean> {
    if (live == null) {
        return false
    }

    const needsScan = prepareRecoveredStateForLiveWal({
        state,
        live,
        sab1,
        pageSize,
    })
    if (!needsScan) {
        return false
    }

    let recoveryChangedState = false
    const recovered = scanWalCommittedTransactions(
        walBytes,
        pageSize,
        live.salt0,
        live.salt1,
        state.integratedFrameMax,
        live.mxFrame,
        hashPage
    )

    for (const rec of recovered) {
        const existing = state.pendingDbDeltas.get(rec.frameMax)
        if (existing != null) {
            if (!equalDelta(existing, rec.delta)) {
                enterFatalRuntime(BOOTSTRAP_WAL_DELTA_MISMATCH, 'BOOT_FATAL')
                return recoveryChangedState
            }
            state.integratedFrameMax = Math.max(state.integratedFrameMax, rec.frameMax)
            continue
        }

        if (txMatchesIgnoredIntent(ignoredIntent, live.salt0, live.salt1, rec)) {
            await hydrateRecoveredWalBlobs?.(rec)
            state.integratedFrameMax = Math.max(state.integratedFrameMax, rec.frameMax)
            continue
        }

        const nextSeq = state.localCommitSeq + 1n
        await durable.transaction(async (tx) => {
            for (const [hash, bytes] of rec.blobs) {
                await tx.putBlob(hash, bytes)
            }
            await tx.appendLocalCommit({
                commitSeq: nextSeq,
                workspaceBaseRoot: state.workspaceBaseHead.root,
                workspaceBaseVersion: state.workspaceBaseHead.version,
                workspaceBasePageCount: state.workspacePageCount,
                walSalt0: live.salt0,
                walSalt1: live.salt1,
                frameMax: rec.frameMax,
                delta: rec.delta,
            })
        })

        state.localCommitSeq = nextSeq

        for (const [hash, bytes] of rec.blobs) {
            stagedCas.set(hash, bytes)
        }

        insertPendingDeltaOrdered(state.pendingDbDeltas, rec.frameMax, rec.delta)

        const workspacePageCountRef = { value: state.workspacePageCount }
        applyDeltaIntoWorkspaceOverlay(
            state.workspaceOverlay,
            workspacePageCountRef,
            rec.delta
        )
        state.workspacePageCount = workspacePageCountRef.value

        state.integratedFrameMax = rec.frameMax
        recoveryChangedState = true
    }

    state.lastWalSnapshot = live
    publishFrontierFromState(sab4, state)
    return recoveryChangedState
}

/**
 * §7.9: Reconcile restored DB-view state against the currently visible live
 * WAL snapshot after ghost recovery has finished.
 *
 * Returns true only for the `live == null` path where bootstrap should mark
 * recoveryChangedState and persist a fresh snapshot.
 */
export function reconcileRestoredStateAgainstLiveWal({
    state,
    live,
    sab1,
    sab4,
    pageSize = DEFAULT_PAGE_SIZE,
}: ReconcileRestoredStateAgainstLiveWalArgs): boolean {
    if (live == null) {
        const recoveryChangedState = resetRecoveredStateWhenLiveWalMissing({
            state,
            sab1,
            pageSize,
        })
        publishFrontierFromState(sab4, state)
        return recoveryChangedState
    }

    prepareRecoveredStateForLiveWal({
        state,
        live,
        sab1,
        pageSize,
    })

    const dbPageCountRef = { value: state.dbPageCount }
    applyBackfilledPending(
        sab1,
        state.pendingDbDeltas,
        state.dbOverlay,
        dbPageCountRef,
        live,
        state.integratedFrameMax,
        pageSize
    )
    state.dbPageCount = dbPageCountRef.value
    state.lastWalSnapshot = live

    // A backfilled WAL prefix is already integrated into the visible MainDb,
    // even when bootstrap did not need to synthesize per-frame pending deltas.
    state.integratedFrameMax = Math.max(state.integratedFrameMax, live.nBackfill)

    setDbPageCountAndInvalidateEofIfNeeded(
        sab1,
        state.dbOverlay,
        dbPageCountRef,
        dbPageCountRef.value,
        pageSize
    )
    state.dbPageCount = dbPageCountRef.value

    publishFrontierFromState(sab4, state)
    return false
}

function publishFrontierFromState(
    sab4: SharedArrayBuffer,
    state: Pick<RestoreTargetState, 'currentWalSalt0' | 'currentWalSalt1' | 'integratedFrameMax'>
): void {
    publishFrontierStateToSabIfChanged(sab4, {
        currentWalSalt0: state.currentWalSalt0,
        currentWalSalt1: state.currentWalSalt1,
        integratedFrameMax: state.integratedFrameMax,
    })
}

export function initializeFreshGenerationVolatileState(): FreshGenerationVolatileState {
    return {
        pushBusy: false,
        mergeBusy: false,
        checkpointBusy: false,
        compactBusy: false,
        compactWanted: false,
        checkpointWanted: false,
        checkpointWantedMode: null,
        checkpointInflightSeq: 0,
        checkpointNoProgressCount: 0,
        checkpointQuiescentBusy: false,
        pendingBaseAdvance: null,
        reconcileIntent: null,
        inflightCommitRegistrations: 0,
        headAdvancePreparing: false,
        headAdvancePrepareWaiter: null,
        commitRegistrationDrainWaiter: null,
        snapshotPersistTail: Promise.resolve(),
        snapshotPersistBusy: false,
        snapshotPersistWanted: false,
        remoteRefreshReqSeq: 0n,
        remoteRefreshAppliedReqSeq: 0n,
        syncRetryScheduled: false,
    }
}

export function resetPerConnectionSabStateForBootstrap(sab4: SharedArrayBuffer): void {
    sab4ResetConnSlots(sab4)
}

export function publishRuntimeEpochToSab(sab4: SharedArrayBuffer, runtimeEpoch: bigint): void {
    Atomics.store(sab4RuntimeEpochView(sab4), 0, runtimeEpoch)
}

export function publishControlNotReady(sab4: SharedArrayBuffer): void {
    const scalar = sab4ScalarView(sab4)
    Atomics.store(scalar, Sab4Scalar.CONTROL_READY, 0)
    Atomics.add(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ, 1)
    Atomics.notify(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ)
}

export function publishControlReady(sab4: SharedArrayBuffer): void {
    const scalar = sab4ScalarView(sab4)
    Atomics.store(scalar, Sab4Scalar.CONTROL_READY, 1)
    Atomics.add(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ, 1)
    Atomics.notify(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ)
}

export function publishWriteGateClosed(sab4: SharedArrayBuffer, reason: number): void {
    const scalar = sab4ScalarView(sab4)
    Atomics.store(scalar, Sab4Scalar.WRITE_GATE_REASON, reason)
    Atomics.store(scalar, Sab4Scalar.WRITE_GATE_CLOSED, 1)
    Atomics.add(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ, 1)
    Atomics.notify(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ)
}

export function publishWriteGateOpen(sab4: SharedArrayBuffer): void {
    const scalar = sab4ScalarView(sab4)
    Atomics.store(scalar, Sab4Scalar.WRITE_GATE_REASON, WRITE_GATE_REASON_NONE)
    Atomics.store(scalar, Sab4Scalar.WRITE_GATE_CLOSED, 0)
    Atomics.add(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ, 1)
    Atomics.notify(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ)
}

export function publishCheckpointGateClosed(sab4: SharedArrayBuffer): void {
    const scalar = sab4ScalarView(sab4)
    Atomics.store(scalar, Sab4Scalar.CHECKPOINT_GATE_CLOSED, 1)
    Atomics.add(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ, 1)
    Atomics.notify(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ)
}

export function publishCheckpointGateOpen(sab4: SharedArrayBuffer): void {
    const scalar = sab4ScalarView(sab4)
    Atomics.store(scalar, Sab4Scalar.CHECKPOINT_GATE_CLOSED, 0)
    Atomics.add(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ, 1)
    Atomics.notify(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ)
}

/**
 * Bootstrap begins from a not-ready, gate-closed state. Any previous fatal
 * error must be forgotten by the fresh generation before recovery proceeds.
 */
export function prepareRuntimeForBootstrap(
    sab4: SharedArrayBuffer,
    runtime: BootstrapRuntimeControl,
    writeGateReason: number = WRITE_GATE_REASON_BOOTSTRAP_IN_PROGRESS
): void {
    runtime.syncFatalError = null
    publishControlNotReady(sab4)
    publishWriteGateClosed(sab4, writeGateReason)
    publishCheckpointGateClosed(sab4)
}

/**
 * The fatal flag is only cleared after the fresh generation has already
 * published not-ready, closed gates, and a new runtime epoch.
 */
export function clearFatalFlagOnBoot(sab4: SharedArrayBuffer): void {
    Atomics.store(sab4ScalarView(sab4), Sab4Scalar.FATAL_FLAG, 0)
}

/**
 * Reset transient SAB lock/WAL shadows for bootstrap.
 * This is the in-memory part of §7.0's WAL/bootstrap primer.
 */
export function resetTransientWalSharedMemoryForBootstrap(
    sab2: SharedArrayBuffer,
    sab3: SharedArrayBuffer,
    walBytes: Uint8Array
): void {
    sab3ResetLocks(sab3)

    const sizeView = sab2SizeView(sab2)
    const dataView = new Uint8Array(sab2, SAB2_DATA_OFF, sab2.byteLength - SAB2_DATA_OFF)
    const oldSize = Atomics.load(sizeView, 0)

    if (walBytes.length > dataView.byteLength) {
        throw new RangeError(`WAL shadow too small: need ${walBytes.length}, have ${dataView.byteLength}`)
    }

    dataView.set(walBytes, 0)
    dataView.fill(0, walBytes.length, Math.max(oldSize, walBytes.length))
    Atomics.store(sizeView, 0, walBytes.length)
}

/**
 * Bootstrap must republish the entire DB-view into SAB1, even when the
 * current DB_PAGE_COUNT already happens to match the restored dbPageCount.
 */
export function reinitializeDbViewSabFromCurrentState(
    sab1: SharedArrayBuffer,
    dbPageCount: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    const ctrl = sab1CtrlView(sab1)
    // Bootstrap starts a fresh runtime generation and must clear any stale
    // SAB1 lock values left behind by a crashed or replaced generation.
    // Waiting on prior lock words here would deadlock recovery.
    Atomics.store(ctrl, SAB1_PAGE_TABLE_LOCK_I32, 1)

    try {
        for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
            const meta = sab1SlotMetaView(sab1, slot, pageSize)

            Atomics.store(meta, SlotMetaI32.LOCK, 1)
            try {
                if (Atomics.load(meta, SlotMetaI32.STATE) !== SlotState.EMPTY) {
                    sab1StoreSlotGen(sab1, slot, sab1LoadSlotGen(sab1, slot, pageSize) + 1n, pageSize)
                    Atomics.store(meta, SlotMetaI32.STATE, SlotState.EMPTY)
                    Atomics.store(meta, SlotMetaI32.PGNO, 0)
                    Atomics.add(meta, SlotMetaI32.WAITSEQ, 1)
                    Atomics.notify(meta, SlotMetaI32.WAITSEQ)
                }
            } finally {
                Atomics.store(meta, SlotMetaI32.LOCK, 0)
            }
        }

        Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, dbPageCount)
    } finally {
        Atomics.store(ctrl, SAB1_PAGE_TABLE_LOCK_I32, 0)
    }
}

/**
 * Execute the fresh-generation bootstrap primer from §7.0/§7.1 in the
 * normative order before live WAL probing or snapshot/journal recovery starts.
 */
export function primeFreshBootstrapGeneration({
    sab2,
    sab3,
    sab4,
    runtime,
    walBytes,
    previousRuntimeEpoch = null,
    nowMs,
    writeGateReason = WRITE_GATE_REASON_BOOTSTRAP_IN_PROGRESS,
}: FreshBootstrapPrimerArgs): FreshBootstrapPrimerResult {
    const runtimeEpoch = chooseFreshRuntimeEpoch(previousRuntimeEpoch, nowMs)

    prepareRuntimeForBootstrap(sab4, runtime, writeGateReason)

    const volatileState = initializeFreshGenerationVolatileState()

    resetPerConnectionSabStateForBootstrap(sab4)
    publishRuntimeEpochToSab(sab4, runtimeEpoch)
    clearFatalFlagOnBoot(sab4)
    resetTransientWalSharedMemoryForBootstrap(sab2, sab3, walBytes)

    return {
        runtimeEpoch,
        volatileState,
    }
}

// ── §7.1 Full bootstrap orchestrator types ─────────────────────

/**
 * External hooks that the §7.1 bootstrap orchestrator depends on.
 *
 * These are operations that cannot live inside the bootstrap module itself
 * because they require access to the RPC bridge, a temporary SQLite
 * connection, or other runtime-specific machinery.
 *
 * Each hook corresponds to a specific step in the normative bootstrap
 * sequence where the orchestrator must reach outside its own module.
 */
export interface BootstrapOrchestratorHooks {
    /**
     * §7.0: Open a temporary bootstrap-recovery SQLite connection against the
     * same database, force WAL recovery from the durable WAL file, and sync
     * the reconstructed SHM/wal-index view into SAB3.
     *
     * Must complete before tryReadWalSnapshotStableOrNull() may be used.
     */
    primeLiveWalViewForBootstrap(): Promise<void>

    /**
     * §7.1: Read a stable WAL snapshot from the already-primed SAB3.
     * Returns null if no valid snapshot could be obtained.
     *
     * Must only be called after primeLiveWalViewForBootstrap() has completed.
     */
    tryReadWalSnapshotStableOrNull(): WalSnapshot | null

    /**
     * §7.1 (fresh-from-remote path): Get the current public head from
     * SecureBlob via the RPC bridge.
     */
    rpcGetHeadVersioned(): Promise<PublicHead>

    /**
     * §7.1 (post-bootstrap): Trigger a remote head refresh so that
     * maybeStartPushOrImport() can pick up the current remote state.
     */
    refreshRemoteHead(): void

    /**
     * §7.1: Enter fatal runtime state. Used by the orchestrator when
     * bootstrap encounters an unrecoverable error.
     */
    enterFatalRuntime(error: unknown, reason: string): void

    /**
     * §7.1: Capture a durable snapshot from the current runtime state.
     * Bridges to snapshot-persistence.captureDurableSnapshot().
     */
    captureDurableSnapshot(): DurableLocalSnapshot

    /**
     * §7.1: Hash a page for WAL scanning. Bridges to wal-scan.hashPage().
     */
    hashPage: HashPageFn

    /** Optional host-level progress hook for long-running bootstrap diagnostics. */
    reportProgress?(message: string, details?: Record<string, unknown>): void
}

/**
 * SAB buffers needed by the bootstrap orchestrator for gate publication,
 * frontier publication, and DB-view SAB reinitialization.
 */
export interface BootstrapOrchestratorSabs {
    sab1: SharedArrayBuffer
    sab2: SharedArrayBuffer
    sab3: SharedArrayBuffer
    sab4: SharedArrayBuffer
}

/**
 * Mutable runtime state that the bootstrap orchestrator modifies in place.
 *
 * This extends ControlWorkerState (DB-view + workspace + WAL) with the
 * additional fields needed during bootstrap: remote head, fatal state,
 * runtime epoch, and base-advance barrier state.
 *
 * The orchestrator writes into this object directly; callers should
 * extract the modified state after runBootstrap() returns.
 */
export interface BootstrapMutableState extends ControlWorkerState, FreshGenerationVolatileState {
    /** §13: Remote head, initialized during bootstrap. */
    remoteHead: PublicHead
    /** §11.2: Fatal error, null while bootstrap is healthy. */
    syncFatalError: unknown | null
    /** §7.0: Runtime epoch, set by primeFreshBootstrapGeneration(). */
    runtimeEpoch: bigint
    /** §7.1: Set to true when bootstrap had to synthesize or repair state. */
    recoveryChangedState: boolean
    /** §7.3/§7.8: Hydrated page blobs used by replay and ghost recovery. */
    stagedCas: Map<PageHash, Uint8Array>
}

/**
 * Result of the §7.1 bootstrap orchestrator.
 *
 * SUCCESS means the runtime is fully bootstrapped and ready for normal
 * operation — DB-view SAB is published, frontier is published, gates are
 * open, and CONTROL_READY = 1.
 *
 * FATAL means bootstrap encountered an unrecoverable error; the runtime
 * must not proceed to normal operation. Fatal state has already been
 * published to SAB (FATAL_FLAG = 1, gates closed, waiters notified).
 */
export type BootstrapResult =
    | { kind: 'SUCCESS' }
    | { kind: 'FATAL'; error: unknown }

/**
 * Arguments for the §7.1 runBootstrap() orchestrator.
 *
 * All individual bootstrap helpers exist as exported functions in this
 * module; runBootstrap() composes them in the normative 9-step order:
 *
 *   1. primeFreshBootstrapGeneration()          (§7.0)
 *   2. load snapshot, journal tail, intent       (§6)
 *   3. primeLiveWalViewForBootstrap()            (§7.0)
 *   4. restoreSnapshotOrSynthesizeBase()         (§7.2)
 *   5. verify restored local generation vs remote (§7.2.5)
 *   6. replayDurableLocalCommitJournal()         (§7.3)
 *   7. resolveIntentDisposition()                (§7.4)
 *   8. verifyAppendingIntentAndMaybePromote()    (§7.5)
 *   9. applyCommittedIntentToLiveState()         (§7.6)
 *  10. recoverGhostLocalCommitsFromWal()         (§7.8)
 *  11. reconcileRestoredStateAgainstLiveWal()    (§7.9)
 *  12. write recovery snapshot + clear intent    (§6.3/§6.4)
 *  13. publish DB-view SAB, frontier, gates      (§4/§8)
 *  14. CONTROL_READY = 1 + refreshRemoteHead()   (§4.4/§13)
 *
 * The ordering is normative. Steps 6–8 are conditional on loaded intent
 * state. Steps 4 and 5 handle the no-snapshot case (synthesis from
 * journal entries or intent).
 */
export interface RunBootstrapArgs {
    /** SAB buffers for gate/frontier/DB-view publication. */
    sabs: BootstrapOrchestratorSabs
    /** Mutable runtime state. Modified in place by the orchestrator. */
    state: BootstrapMutableState
    /** Durable store for snapshot, journal, and intent I/O. */
    durable: DurableLocalStore
    /** External hooks for RPC, WAL priming, and fatal handling. */
    hooks: BootstrapOrchestratorHooks
    /** Durable WAL file bytes, for SAB2 shadow priming. */
    walBytes: Uint8Array
    /** Previous runtime epoch (if this is a re-bootstrap in the same process). */
    previousRuntimeEpoch?: bigint | null
    /** Optional wall-clock input used to derive the fresh runtime epoch. */
    nowMs?: number
    /** Page size for WAL scanning. */
    pageSize?: number
}

const BOOTSTRAP_FATAL_REASON = 'BOOT_FATAL'

/**
 * §7.1 bootstrap success epilog.
 *
 * Re-publishes the recovered DB-view/frontier and only then opens normal
 * runtime operation for readers and committers.
 */
function finalizeBootstrapSuccess(
    sabs: BootstrapOrchestratorSabs,
    state: Pick<BootstrapMutableState, 'dbPageCount' | 'runtimeEpoch' | 'currentWalSalt0' | 'currentWalSalt1' | 'integratedFrameMax'>,
    refreshRemoteHead: () => void,
    pageSize: number
): BootstrapResult {
    reinitializeDbViewSabFromCurrentState(sabs.sab1, state.dbPageCount, pageSize)
    publishRuntimeEpochToSab(sabs.sab4, state.runtimeEpoch)
    publishFrontierFromState(sabs.sab4, state)
    publishCheckpointGateOpen(sabs.sab4)
    publishWriteGateOpen(sabs.sab4)
    publishControlReady(sabs.sab4)
    refreshRemoteHead()
    return { kind: 'SUCCESS' }
}

/**
 * Hydrate journal page hashes into stagedCas before replay.
 *
 * Replay is synchronous by design, so the orchestrator performs the durable
 * blob I/O ahead of each entry and then reuses the existing replay helper.
 */
async function hydrateJournalEntryBlobsIntoStagedCas(
    entry: DurableLocalCommitJournalEntry,
    durable: Pick<DurableLocalStore, 'getBlob'>,
    stagedCas: Map<PageHash, Uint8Array>
): Promise<void> {
    for (const hash of entry.delta.pageHashes.values()) {
        if (stagedCas.has(hash)) {
            continue
        }

        const bytes = await durable.getBlob(hash)
        if (bytes == null) {
            throw new Error(`missing durable blob for journal hash ${hash}`)
        }

        stagedCas.set(hash, bytes)
    }
}

/**
 * §7.1 full bootstrap orchestration.
 *
 * Composes the already-exported §7.x helpers in the normative order, handles
 * the fresh-from-remote fallback, and only opens CONTROL_READY after the
 * recovered DB-view/frontier has been fully republished.
 */
export async function runBootstrap({
    sabs,
    state,
    durable,
    hooks,
    walBytes,
    previousRuntimeEpoch = null,
    nowMs,
    pageSize = DEFAULT_PAGE_SIZE,
}: RunBootstrapArgs): Promise<BootstrapResult> {
    const enterFatalRuntime = (error: unknown, reason: string = BOOTSTRAP_FATAL_REASON): void => {
        hooks.enterFatalRuntime(error, reason)
        if (state.syncFatalError == null) {
            state.syncFatalError = error
        }
    }

    const finishFatal = (fallbackError?: unknown): BootstrapResult => {
        const error = state.syncFatalError ?? fallbackError
        if (error == null) {
            throw new Error('bootstrap fatal exit requires an error')
        }
        return { kind: 'FATAL', error }
    }

    try {
        const primer = primeFreshBootstrapGeneration({
            sab2: sabs.sab2,
            sab3: sabs.sab3,
            sab4: sabs.sab4,
            runtime: state,
            walBytes,
            previousRuntimeEpoch,
            nowMs,
            writeGateReason: WRITE_GATE_REASON_BOOTSTRAP_IN_PROGRESS,
        })
        state.runtimeEpoch = primer.runtimeEpoch
        Object.assign(state, primer.volatileState)
        state.recoveryChangedState = false

        hooks.reportProgress?.('Loading durable bootstrap state')
        const snap = await durable.loadSnapshot()
        const journalTailIfNoSnap = snap == null
            ? await durable.readJournalAfter(-1n)
            : []
        const loadedIntent = await durable.loadReconcileIntentOrNull()
        hooks.reportProgress?.('Loaded durable bootstrap state', {
            hasSnapshot: snap != null,
            journalTailEntries: journalTailIfNoSnap.length,
            hasReconcileIntent: loadedIntent != null,
        })

        hooks.reportProgress?.('Priming live WAL view')
        await hooks.primeLiveWalViewForBootstrap()
        if (state.syncFatalError != null) {
            return finishFatal()
        }
        const live = hooks.tryReadWalSnapshotStableOrNull()
        hooks.reportProgress?.('Primed live WAL view', {
            hasLiveSnapshot: live != null,
        })

        if (snap == null && journalTailIfNoSnap.length === 0 && loadedIntent == null) {
            hooks.reportProgress?.('Reading remote sqlite3mt head')
            const head0 = await hooks.rpcGetHeadVersioned()
            hooks.reportProgress?.('Read remote sqlite3mt head', {
                version: head0.version.toString(),
                pageCount: head0.root.meta.pageCount,
            })
            if (state.syncFatalError != null) {
                return finishFatal()
            }

            state.dbBaseRoot = head0.root
            state.dbOverlay.clear()
            state.dbPageCount = head0.root.meta.pageCount

            state.workspaceBaseHead = {
                root: head0.root,
                version: head0.version,
            }
            state.workspaceOverlay.clear()
            state.workspacePageCount = head0.root.meta.pageCount

            state.remoteHead = {
                root: head0.root,
                version: head0.version,
            }

            state.pendingDbDeltas.clear()
            state.currentWalSalt0 = null
            state.currentWalSalt1 = null
            state.integratedFrameMax = 0
            state.lastWalSnapshot = null
            state.localCommitSeq = 0n

            hooks.reportProgress?.('Writing fresh sqlite3mt durable snapshot')
            await durable.writeSnapshot(hooks.captureDurableSnapshot())
            if (state.syncFatalError != null) {
                return finishFatal()
            }

            hooks.reportProgress?.('Finalizing fresh sqlite3mt bootstrap')
            return finalizeBootstrapSuccess(sabs, state, hooks.refreshRemoteHead, pageSize)
        }

        hooks.reportProgress?.('Restoring sqlite3mt durable snapshot or synthesized base')
        restoreSnapshotOrSynthesizeBase({
            state,
            snap,
            journalTailIfNoSnap,
            intent: loadedIntent,
            enterFatalRuntime,
        })
        if (state.syncFatalError != null) {
            return finishFatal()
        }

        hooks.reportProgress?.('Reading authoritative remote sqlite3mt head')
        const authoritativeRemoteHead = await hooks.rpcGetHeadVersioned()
        hooks.reportProgress?.('Read authoritative remote sqlite3mt head', {
            version: authoritativeRemoteHead.version.toString(),
            pageCount: authoritativeRemoteHead.root.meta.pageCount,
        })
        if (state.syncFatalError != null) {
            return finishFatal()
        }

        if (
            authoritativeRemoteHead.version === state.workspaceBaseHead.version
            && !sameRoot(authoritativeRemoteHead.root, state.workspaceBaseHead.root)
        ) {
            enterFatalRuntime(
                BOOTSTRAP_PROTOCOL_VIOLATION_SAME_VERSION_DIFFERENT_ROOT,
                BOOTSTRAP_FATAL_REASON
            )
            return finishFatal()
        }

        if (remoteHeadRewindsRestoredLocalState(state.workspaceBaseHead, authoritativeRemoteHead)) {
            await durable.clearAllLocalState()
            resetBootstrapStateToRemoteHead(state, authoritativeRemoteHead)
            await durable.writeSnapshot(hooks.captureDurableSnapshot())
            if (state.syncFatalError != null) {
                return finishFatal()
            }
            return finalizeBootstrapSuccess(sabs, state, hooks.refreshRemoteHead, pageSize)
        }

        const tail = snap != null
            ? await durable.readJournalAfter(snap.localCommitSeq)
            : journalTailIfNoSnap

        for (const entry of tail) {
            await hydrateJournalEntryBlobsIntoStagedCas(entry, durable, state.stagedCas)
            replayDurableLocalCommitJournal({
                state,
                tail: [entry],
                sab1: sabs.sab1,
                pageSize,
                enterFatalRuntime,
            })
            if (state.syncFatalError != null) {
                return finishFatal()
            }
        }

        const disposition = resolveIntentDisposition(
            state.workspaceBaseHead,
            loadedIntent,
            enterFatalRuntime
        )
        if (state.syncFatalError != null) {
            return finishFatal()
        }

        let activeIntent = disposition.activeIntentOrNull
        const ignoredIntent = disposition.ignoredIntentOrNull

        if (activeIntent != null && activeIntent.phase === 'APPENDING') {
            const promoted = await verifyAppendingIntentAndMaybePromote({
                intent: activeIntent,
                live,
                walBytes,
                durable,
                hashPage: hooks.hashPage,
                enterFatalRuntime,
                pageSize,
            })
            state.recoveryChangedState = state.recoveryChangedState || promoted
            if (state.syncFatalError != null) {
                return finishFatal()
            }
            activeIntent = promoted ? activeIntent : null
        }

        if (activeIntent != null && activeIntent.phase === 'COMMITTED') {
            applyCommittedIntentToLiveState({
                intent: activeIntent,
                state,
                sab1: sabs.sab1,
                pageSize,
            })
            state.recoveryChangedState = true
        }

        if (state.syncFatalError == null) {
            const ghostRecovered = await recoverGhostLocalCommitsFromWal({
                state,
                live,
                ignoredIntent,
                walBytes,
                durable,
                stagedCas: state.stagedCas,
                hashPage: hooks.hashPage,
                sab1: sabs.sab1,
                sab4: sabs.sab4,
                pageSize,
                enterFatalRuntime,
            })
            state.recoveryChangedState = state.recoveryChangedState || ghostRecovered
        }
        if (state.syncFatalError != null) {
            return finishFatal()
        }

        const reconciled = reconcileRestoredStateAgainstLiveWal({
            state,
            live,
            sab1: sabs.sab1,
            sab4: sabs.sab4,
            pageSize,
        })
        state.recoveryChangedState = state.recoveryChangedState || reconciled
        if (state.syncFatalError != null) {
            return finishFatal()
        }

        if (snap == null || state.recoveryChangedState) {
            await durable.writeSnapshot(hooks.captureDurableSnapshot())
            if (state.syncFatalError != null) {
                return finishFatal()
            }
        }

        if (loadedIntent != null) {
            await durable.clearReconcileIntent(loadedIntent.intentId)
            if (state.syncFatalError != null) {
                return finishFatal()
            }
        }

        state.pendingBaseAdvance = null
        state.reconcileIntent = null
        state.remoteHead = {
            root: state.workspaceBaseHead.root,
            version: state.workspaceBaseHead.version,
        }

        return finalizeBootstrapSuccess(sabs, state, hooks.refreshRemoteHead, pageSize)
    } catch (error) {
        if (state.syncFatalError == null) {
            enterFatalRuntime(error, BOOTSTRAP_FATAL_REASON)
        }
        return finishFatal(error)
    }
}
